/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller, { inject as controller } from '@ember/controller';
import { TrackedArray } from 'tracked-built-ins';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { countTypes } from './lists/class-lists/basic-class-counts';
import moment from 'moment';
import { alias } from '@ember/object/computed';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  byKeys,
  filterBy,
  mapBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';

export default class ListBuilderController extends Controller {
  @action
  set_selectedTerm(value) {
    this._selectedTerm = value;
  }

  @action
  setCountType(value) {
    this.countType = value;
  }

  @action
  setDate(value) {
    this.date = value;
  }

  @action
  setDentalEndDate(value) {
    this.dentalEndDate = value;
  }

  @action
  setDentalStartDate(value) {
    this.dentalStartDate = value;
  }

  @action
  setIncludeCurrent(value) {
    this.includeCurrent = value;
  }

  @action
  setIncludeCurrentStaff(value) {
    this.includeCurrentStaff = value;
  }

  @action
  setIncludeFormer(value) {
    this.includeFormer = value;
  }

  @action
  setIncludeFormerStaff(value) {
    this.includeFormerStaff = value;
  }

  @action
  setIncludePre(value) {
    this.includePre = value;
  }

  @action
  setLeftAfterDate(value) {
    this.leftAfterDate = value;
  }

  @action
  setLeftBeforeDate(value) {
    this.leftBeforeDate = value;
  }

  @action
  setSelectedDate(value) {
    this.selectedDate = value;
  }

  @action
  setSelectedTerm(value) {
    this.selectedTerm = value;
  }

  @action
  setTab(value) {
    this.tab = value;
  }

  queryParams = ['tab'];

  @service
  session;

  @service
  dates;

  @service
  features;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  roles;

  @service
  router;

  @controller('admin.list')
  listController;
  DataType = DataType;
  GroupType = GroupType;
  PersonType = PersonType;
  Scopes = Scopes;
  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get useSecondaryContacts() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @tracked includeCurrent = true;
  @tracked includePre = false;
  @tracked includeFormer = false;
  @tracked includeFormerStaff = false;
  @tracked includeCurrentStaff = true;
  closeRollDialogOpen = false;
  @tracked
  showStudentProfileImage = false;
  @tracked
  showStaffProfileImage = false;

  @alias('session.school')
  school;

  @optionAlias('nzMOE:boardElections:mainRollClosingDate')
  mainRollClosingDate;
  @optionAlias('nzMOE:boardElections:supplementaryRollClosingDate')
  supplementaryRollClosingDate;
  @tracked
  date = moment().format('YYYY-MM-DD');
  @tracked
  dentalStartDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
  @tracked
  dentalEndDate = moment().format('YYYY-MM-DD');
  @tracked
  leftAfterDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
  @tracked
  leftBeforeDate = moment().format('YYYY-MM-DD');
  @tracked
  rollDate = moment().format('YYYY-MM-DD');
  @tracked
  selectedDate = moment().format('YYYY-MM-DD');

  @computed('dates.previousAcademicYearLabel')
  get previousAcademicDates() {
    let previousYear = this.dates.previousAcademicYearLabel;
    let academicYearDates =
      this.dates.halfyearDatesForAcademicYear(previousYear);
    return {
      startDate: academicYearDates.startDate.format('YYYY-MM-DD'),
      endDate: academicYearDates.endDate.format('YYYY-MM-DD'),
    };
  }

  @computed('session.school.fields.core:groups:teams.value')
  get teams() {
    return filterBy(
      this.get('session.school.fields.core:groups:teams.value'),
      'value',
    ).sort(byKeys('value'));
  }

  @tracked _selectedTerm = null;

  @computed('model.terms.[]', '_selectedTerm')
  get selectedTerm() {
    if (this._selectedTerm) {
      return this._selectedTerm;
    }
    let term = this.model.terms.find(
      (term) => this.date >= term.start && this.date <= term.end,
    );
    if (isEmpty(term)) {
      let candidates = this.model.terms
        .filter((term) => this.date >= term.start)
        .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
      term = candidates[candidates.length - 1];
    }
    return term;
  }

  // TODO: put count types in a common location?
  countTypes = countTypes;

  @tracked
  countType = countTypes[0];
  @tracked tab = '' + PersonType.LEARNER;

  get isLearnerTab() {
    return this.tab === '' + PersonType.LEARNER;
  }

  @computed('model.fieldGroups.[]', 'tab')
  get fieldGroups() {
    let tab = parseInt(this.tab, 10);
    let groups = this.get('model.fieldGroups')
      .filter((group) => group.personType === tab)
      .sort(
        (a, b) =>
          a.sequence - b.sequence || (a.name || '').localeCompare(b.name || ''),
      );

    // Shift medical conditions group to sit after medical/wellbeing.
    let medicalConditionGroup = groups.find(
      (group) => group.key === 'medicalCondition',
    );
    if (isEmpty(medicalConditionGroup)) {
      // No conditions group - just return as-is.
      return groups;
    }
    let medicalGroup = groups.find((group) => group.key === 'medical');
    if (isEmpty(medicalGroup)) {
      // No medical group - try wellbeing.
      medicalGroup = groups.find((group) => group.key === 'wellbeing');
    }
    if (isEmpty(medicalGroup)) {
      // No medical/wellbeing group - return as-is.
      return groups;
    }

    // We have a medical/wellbeing group - shift medical condition to after it.
    groups = groups.filter((group) => group !== medicalConditionGroup);
    let medicalIndex = groups.indexOf(medicalGroup);
    groups.splice(medicalIndex + 1, 0, medicalConditionGroup);
    return groups;
  }
  // caregiverFields is a combined, sorted array of caregiver ("contact") fields.
  @computed('model.fieldGroups.[]')
  get caregiverFields() {
    let fields = [];
    let groups = this.get('model.fieldGroups')
      .filter((group) => group.personType === PersonType.CAREGIVER)
      .sort(
        (a, b) =>
          a.sequence - b.sequence || (a.name || '').localeCompare(b.name || ''),
      );
    groups.forEach(function (group) {
      fields.push(
        ...resolved(group.fields)
          .filter((field) => field.key !== 'nzMOE:botVotingUpdateDate')
          .sort(
            (a, b) =>
              a.sequence - b.sequence ||
              (a.label || '').localeCompare(b.label || ''),
          ),
      );
    });

    if (!this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // School staff can only see fields which they have the role for.
      fields = fields.filter((field) => {
        if (isEmpty(field.requiredRole)) {
          // No role required; include this field.
          return true;
        }
        return this.roles.has(field.requiredRole);
      });
    }

    return fields;
  }

  // medicalFields is a combined, sorted array of medical ("contact") fields.
  @computed('model.fieldGroups.[]')
  get medicalFields() {
    let fields = [];
    let groups = this.get('model.fieldGroups')
      .filter(
        (group) => group.personType === PersonType.DOCTORS_AND_SPECIALISTS,
      )
      .sort(
        (a, b) =>
          a.sequence - b.sequence || (a.name || '').localeCompare(b.name || ''),
      );
    groups.forEach(function (group) {
      fields.push(
        ...resolved(group.fields)
          .slice()
          .sort(
            (a, b) =>
              a.sequence - b.sequence ||
              (a.label || '').localeCompare(b.label || ''),
          ),
      );
    });
    return fields;
  }

  // conditionFields is a list of medical condition dynamic fields.
  @computed('model.fieldGroups.[]')
  get conditionFields() {
    let conditionGroup = this.get('model.fieldGroups')
      .filter((group) => group.personType === PersonType.LEARNER)
      .find((group) => group.key === 'medicalCondition');
    return resolved(conditionGroup.fields)
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('conditionFields.@each.key')
  get conditionSubtypeField() {
    return this.conditionFields.find((field) => field.key === 'core:subtype');
  }

  conditionExtraFields = Object.freeze([
    {
      key: 'type',
      label: 'Type of Condition',
    },
    {
      key: 'priority',
      label: 'Priority',
    },
    {
      key: 'description',
      label: 'Description',
    },
    {
      key: 'anaphylaxis',
      label: 'Anaphylaxis',
    },
    {
      key: 'highRisk',
      label: 'High Risk',
    },
    {
      key: 'prescribedMedicationHeld',
      label: 'Prescribed Medication Held Onsite',
    },
    {
      key: 'startDate',
      label: 'Start date',
    },
    {
      key: 'reviewDate',
      label: 'Review date',
    },
    {
      key: 'endDate',
      label: 'End date',
    },
  ]);

  enrolmentFields = Object.freeze([
    {
      key: 'startDate',
      label: 'First attendance date',
    },
    {
      key: 'endDate',
      label: 'Last attendance date',
    },
    {
      key: 'reasonForLeaving',
      label: 'Reason for Leaving',
    },
    {
      key: 'destinationSchool',
      label: 'Destination School',
    },
    {
      key: 'postSchoolActivity',
      label: 'Post-school Activity',
    },
  ]);

  medicalContactFields = Object.freeze([
    {
      key: 'relationshipName',
      label: 'Contact type',
      additional: true,
    },
    {
      key: 'priority',
      label: 'Contact priority',
      additional: true,
    },
    {
      key: 'notes',
      label: 'Notes',
      additional: true,
    },
    {
      key: 'permissionToContact',
      label: 'Permission to contact',
      additional: true,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      additional: true,
    },
    {
      key: 'endDate',
      label: 'End Date',
      additional: true,
    },
  ]);

  contactFields = Object.freeze([
    {
      key: 'relationshipName',
      label: 'Relationship to student',
      additional: true,
    },
    {
      key: 'priority',
      label: 'Contact priority',
      additional: true,
    },
    {
      key: 'notes',
      label: 'Notes',
      additional: true,
    },
    {
      key: 'allowedAccess',
      label: 'Allowed access to Hero app',
      additional: true,
    },
    {
      key: 'receivesSms',
      label: 'Receives Text Messages',
      additional: true,
    },
    {
      key: 'billPayer',
      label: 'Has Finance Access',
      additional: true,
    },
  ]);

  dateCalculationFields = Object.freeze([
    {
      key: 'ageOnDate',
      label: 'Age on selected date',
    },
    {
      key: 'monthsAtSchoolOnDate',
      label: 'Months at school on selected date',
    },
    {
      key: 'weeksAtSchoolOnDateExcludingHolidays',
      label: 'Weeks at school on selected date (Excluding Holidays)',
    },
    {
      key: 'weeksAtSchoolOnDateIncludingHolidays',
      label: 'Weeks at school on selected date (Including Holidays)',
    },
    {
      key: 'birthday',
      label: 'Birthday',
    },
  ]);

  permissionFields = Object.freeze([
    {
      key: 'isAdmin',
      label: 'Is Admin',
    },
    {
      key: 'hasHelpdesk',
      label: 'Has Helpdesk Access',
    },
    {
      key: 'pageAccessLevel',
      label: 'Page Access Level',
    },
    {
      key: 'restrictedFieldLabels',
      label: 'Restricted Field Access',
    },
    {
      key: 'staffRoleLabels',
      label: 'Additional Permissions',
    },
  ]);

  @computed('fieldGroups.[]', 'session.{scopes.[],roles.[]}')
  get visibleFieldsByGroup() {
    return this.fieldGroups.reduce((map, group) => {
      let fields = resolved(group.fields)
        .slice()
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
      if (!this.permissions.meet(Scopes.LINCED_MANAGER)) {
        // School staff can only see fields which they have the role for.
        fields = fields.filter((field) => {
          if (isEmpty(field.requiredRole)) {
            // No role required; include this field.
            return true;
          }
          return this.roles.has(field.requiredRole);
        });
      }
      map[group.id] = fields;
      return map;
    }, {});
  }

  // TrackedArray, not a plain array: these are mutated in place (push/
  // splice via toggleFieldSelected/clearImpl below) without ever being
  // reassigned, and are read via the `includes` template helper as
  // checkbox state - that needs to stay independently reactive. Not
  // @tracked fields: TrackedArray provides its own internal reactivity,
  // and the field itself is never reassigned to a new array.
  selectedLearnerFields = new TrackedArray();
  selectedEnrolmentFields = new TrackedArray();
  selectedLearnerExtraFields = new TrackedArray();
  selectedStaffExtraFields = new TrackedArray();
  selectedPrimaryExtraFields = new TrackedArray();
  selectedSecondaryExtraFields = new TrackedArray();
  selectedAdditionalExtraFields = new TrackedArray();
  selectedStaffFields = new TrackedArray();
  selectedDateCalculationFields = new TrackedArray();

  get selectedFields() {
    switch (parseInt(this.tab, 10)) {
      case PersonType.LEARNER:
        return this.selectedLearnerFields;
      case PersonType.STAFF:
        return this.selectedStaffFields;
      default:
        return [];
    }
  }

  get selectedExtraFields() {
    switch (parseInt(this.tab, 10)) {
      case PersonType.LEARNER:
        return this.selectedLearnerExtraFields;
      case PersonType.STAFF:
        return this.selectedStaffExtraFields;
      default:
        return [];
    }
  }

  @computed('fieldGroups.[]')
  get preSelectedFields() {
    return this.fieldGroups.reduce((selected, group) => {
      let fields = resolved(group.fields);
      if (!isEmpty(fields)) {
        let found = fields.filter((field) => {
          return (
            field.key === 'core:firstNamePreferred' ||
            field.key === 'core:lastNamePreferred'
          );
        });
        selected.push(...found);
      }
      return selected;
    }, []);
  }

  // Plain getter, not @computed: every dependent key here is TrackedArray-
  // backed, and a classic @computed doesn't reliably observe TrackedArray's
  // autotracked reads the way it does a real EmberArray's.
  get allSelectedFields() {
    let allSelectedFields = [];
    switch (parseInt(this.tab, 10)) {
      case PersonType.LEARNER:
        allSelectedFields.push(...this.selectedLearnerFields);
        allSelectedFields.push(...this.selectedEnrolmentFields);
        allSelectedFields.push(...this.selectedExtraFields);
        allSelectedFields.push(...this.selectedMedicalContactFields);
        allSelectedFields.push(...this.selectedMedicalExtraFields);
        allSelectedFields.push(...this.selectedPrimaryContactFields);
        allSelectedFields.push(...this.selectedPrimaryExtraFields);
        allSelectedFields.push(...this.selectedSecondaryContactFields);
        allSelectedFields.push(...this.selectedSecondaryExtraFields);
        allSelectedFields.push(...this.selectedAdditionalContactFields);
        allSelectedFields.push(...this.selectedAdditionalExtraFields);
        allSelectedFields.push(...this.selectedDateCalculationFields);
        break;
      case PersonType.STAFF:
        allSelectedFields.push(...this.selectedStaffFields);
        allSelectedFields.push(...this.selectedExtraFields);
        break;
    }

    return allSelectedFields;
  }

  selectedMedicalContactFields = new TrackedArray();
  selectedMedicalExtraFields = new TrackedArray();
  selectedConditionFields = new TrackedArray();
  selectedConditionExtraFields = new TrackedArray();
  selectedPrimaryContactFields = new TrackedArray();
  selectedSecondaryContactFields = new TrackedArray();
  selectedAdditionalContactFields = new TrackedArray();

  // Plain getter, not @computed: same reason as allSelectedFields above -
  // selectedExtraFields/selectedFields are TrackedArray-backed.
  get selectionIsValid() {
    if (
      this.selectedFields.length < 1 &&
      !this.selectedExtraFields.includes('heroId')
    ) {
      // At least one field must be selected.
      return false;
    }
    if (this.tab === '' + PersonType.LEARNER) {
      // For learners, at least one enrolment status must also be selected.
      return this.includeCurrent || this.includePre || this.includeFormer;
    } else if (this.tab === '' + PersonType.STAFF) {
      // For staff list, at least one employment status must be selected.
      return this.includeCurrentStaff || this.includeFormerStaff;
    }
    // We've got a valid selection.
    return true;
  }

  @computed('model.groups')
  get homerooms() {
    return this.model.groups.filter(
      (group) => group.type === GroupType.HOMEROOM,
    );
  }

  @optionAlias('core:staffPermissions:hideNextYearsPlacementsForTeachers')
  hideNextYearsPlacementsForTeachers;

  @tracked
  botDialogOpen;

  @computed('session.scopes.[]')
  get hasAdminAccess() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @computed('hasAdminAccess', 'hideNextYearsPlacementsForTeachers')
  get disableNextYear() {
    return !this.hasAdminAccess && this.hideNextYearsPlacementsForTeachers;
  }

  @action
  onChangeRollDate(value) {
    // Restrict to current year.
    if (moment(value).year() === moment(this.date).year()) {
      this.rollDate = value;
    }
  }

  @action
  toggleField(field) {
    this.toggleFieldSelected(field, this.selectedFields);
  }

  @action
  toggleEnrolmentField(field) {
    this.toggleFieldSelected(field, this.selectedEnrolmentFields);
  }

  @action
  toggleExtraField(field) {
    this.toggleFieldSelected(field, this.selectedExtraFields);
  }

  @action
  toggleMedicalContactField(field) {
    this.toggleFieldSelected(field, this.selectedMedicalContactFields);
  }

  @action
  toggleMedicalContactExtraField(field) {
    this.toggleFieldSelected(field, this.selectedMedicalExtraFields);
  }

  @action
  toggleConditionField(field) {
    this.toggleFieldSelected(field, this.selectedConditionFields);
  }

  @action
  toggleConditionExtraField(field) {
    this.toggleFieldSelected(field, this.selectedConditionExtraFields);
  }

  @action
  togglePrimaryContactField(field) {
    this.toggleFieldSelected(field, this.selectedPrimaryContactFields);
  }

  @action
  togglePrimaryExtraField(field) {
    this.toggleFieldSelected(field, this.selectedPrimaryExtraFields);
  }

  @action
  toggleSecondaryContactField(field) {
    this.toggleFieldSelected(field, this.selectedSecondaryContactFields);
  }

  @action
  toggleSecondaryExtraField(field) {
    this.toggleFieldSelected(field, this.selectedSecondaryExtraFields);
  }

  @action
  toggleAdditionalContactField(field) {
    this.toggleFieldSelected(field, this.selectedAdditionalContactFields);
  }

  @action
  toggleAdditionalExtraField(field) {
    this.toggleFieldSelected(field, this.selectedAdditionalExtraFields);
  }

  @action
  toggleDateCalculationField(field) {
    this.toggleFieldSelected(field, this.selectedDateCalculationFields);
  }

  @action
  generateList() {
    let type = parseInt(this.tab, 10);
    let fieldKeys = mapBy(this.selectedFields, 'key');
    let params = {
      type,
      fields: fieldKeys.join('|'),
      // All params must be specified / nulled out to prevent previous values from remaining applied.
      enrolment: null,
      extra: null,
      medical: null,
      medicalExtra: null,
      condition: null,
      conditionExtra: null,
      primary: null,
      primaryExtra: null,
      additional: null,
      additionalExtra: null,
      dateCalculation: null,
      dateCalculationDate: null,
      includeNonCurrentMedical: false,
    };

    let medicalKeys = '';
    let conditionKeys = '';
    let primaryKeys = '';
    let secondaryKeys = '';
    let additionalKeys = '';
    switch (type) {
      case PersonType.LEARNER:
        // Add enrolment status flags
        params.includeCurrent = this.includeCurrent;
        params.includePre = this.includePre;
        params.includeFormer = this.includeFormer;
        params.showProfileImage = this.showStudentProfileImage;

        // Add extra (i.e. calculated) fields, if any were chosen
        if (!isEmpty(this.selectedExtraFields)) {
          params.extra = this.selectedExtraFields.join('|');
        }
        if (!isEmpty(this.selectedEnrolmentFields)) {
          params.enrolment = this.selectedEnrolmentFields.join('|');
        }

        // Add contact and medical condition fields, if any were chosen.
        medicalKeys = mapBy(this.selectedMedicalContactFields, 'key');
        conditionKeys = mapBy(this.selectedConditionFields, 'key');
        primaryKeys = mapBy(this.selectedPrimaryContactFields, 'key');
        secondaryKeys = mapBy(this.selectedSecondaryContactFields, 'key');
        additionalKeys = mapBy(this.selectedAdditionalContactFields, 'key');
        if (!isEmpty(medicalKeys)) {
          params.medical = medicalKeys.join('|');
        }
        if (!isEmpty(this.selectedMedicalExtraFields)) {
          params.medicalExtra = this.selectedMedicalExtraFields.join('|');
        }
        if (!isEmpty(conditionKeys)) {
          params.condition = conditionKeys.join('|');
        }
        if (!isEmpty(this.selectedConditionExtraFields)) {
          params.conditionExtra = this.selectedConditionExtraFields.join('|');
        }
        if (!isEmpty(primaryKeys)) {
          params.primary = primaryKeys.join('|');
        }
        if (!isEmpty(this.selectedPrimaryExtraFields)) {
          params.primaryExtra = this.selectedPrimaryExtraFields.join('|');
        }
        if (!isEmpty(secondaryKeys)) {
          params.secondary = secondaryKeys.join('|');
        }
        if (!isEmpty(this.selectedSecondaryExtraFields)) {
          params.secondaryExtra = this.selectedSecondaryExtraFields.join('|');
        }
        if (!isEmpty(additionalKeys)) {
          params.additional = additionalKeys.join('|');
        }
        if (!isEmpty(this.selectedAdditionalExtraFields)) {
          params.additionalExtra = this.selectedAdditionalExtraFields.join('|');
        }
        if (!isEmpty(this.selectedDateCalculationFields)) {
          params.dateCalculationDate = String(this.selectedDate);
          params.dateCalculation = this.selectedDateCalculationFields.join('|');
        }
        break;

      case PersonType.STAFF:
        // Add extra (i.e. calculated) fields, if any were chosen
        if (!isEmpty(this.selectedExtraFields)) {
          params.extra = this.selectedExtraFields.join('|');
        }
        params.includeFormerStaff = this.includeFormerStaff;
        params.includeCurrentStaff = this.includeCurrentStaff;
        params.showProfileImage = this.showStaffProfileImage;
        break;
    }

    this.router.transitionTo('admin.list', { queryParams: params });
  }

  @action
  clear() {
    this.listController.clearSelection();
    this.clearImpl();
  }

  @action
  openBotDialog() {
    this.botDialogOpen = true;
  }

  @action
  confirmCloseRoll(proceed) {
    if (proceed) {
      this.botDialogOpen = false;
      if (
        this.school.get('fields.nzMOE:boardElections:mainRollClosingDate.value')
      ) {
        // Create the supplementaryRollClosingDate field if it doesn't exist
        if (
          !this.school.get(
            'fields.nzMOE:boardElections:supplementaryRollClosingDate',
          )
        ) {
          this.school.addField(
            'nzMOE:boardElections:supplementaryRollClosingDate',
            DataType.STRING,
          );
        }
        this.school.set(
          'fields.nzMOE:boardElections:supplementaryRollClosingDate.value',
          moment(),
        );
      } else {
        // Create the mainRollClosingDate field if it doesn't exist
        if (
          !this.school.get('fields.nzMOE:boardElections:mainRollClosingDate')
        ) {
          this.school.addField(
            'nzMOE:boardElections:mainRollClosingDate',
            DataType.STRING,
          );
        }
        this.school.set(
          'fields.nzMOE:boardElections:mainRollClosingDate.value',
          moment(),
        );
      }

      let school = this.school;
      school.save().then(() => {
        this.paperToaster.show('Roll has been closed', {
          duration: 4000,
          position: 'top right',
        });
      });
    } else {
      this.botDialogOpen = false;
    }
  }

  @action
  completeElection(proceed) {
    if (proceed) {
      this.botDialogOpen = false;
      this.school.set(
        'fields.nzMOE:boardElections:supplementaryRollClosingDate.value',
        null,
      );
      this.school.set(
        'fields.nzMOE:boardElections:mainRollClosingDate.value',
        null,
      );

      let school = this.school;
      school.save().then(() => {
        this.paperToaster.show('Board Election has been completed', {
          duration: 4000,
          position: 'top right',
        });
      });
    } else {
      this.botDialogOpen = false;
    }
  }

  toggleFieldSelected(field, selected) {
    let pos = selected.indexOf(field);
    if (pos > -1) {
      selected.splice(pos, 1);
    } else {
      selected.push(field);
    }
  }

  clearImpl() {
    switch (parseInt(this.tab, 10)) {
      case PersonType.LEARNER:
        this.selectedLearnerFields.length = 0;
        this.selectedEnrolmentFields.length = 0;
        this.selectedExtraFields.length = 0;
        this.selectedMedicalContactFields.length = 0;
        this.selectedMedicalExtraFields.length = 0;
        this.selectedConditionFields.length = 0;
        this.selectedConditionExtraFields.length = 0;
        this.selectedPrimaryContactFields.length = 0;
        this.selectedPrimaryExtraFields.length = 0;
        this.selectedSecondaryContactFields.length = 0;
        this.selectedSecondaryExtraFields.length = 0;
        this.selectedAdditionalContactFields.length = 0;
        this.selectedAdditionalExtraFields.length = 0;
        this.selectedDateCalculationFields.length = 0;
        this.includeCurrent = true;
        this.includePre = false;
        this.includeFormer = false;
        this.showStudentProfileImage = false;
        this.selectedLearnerFields.push(...this.preSelectedFields);

        break;
      case PersonType.STAFF:
        this.selectedStaffFields.length = 0;
        this.selectedStaffExtraFields.length = 0;
        this.includeFormerStaff = false;
        this.includeCurrentStaff = true;
        this.showStaffProfileImage = false;
        break;
    }
  }
}
